import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link as UiLink } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type AdminCategoryUsage,
  useAdminLLMSummaryApi,
  useAdminMatchingStatsApi,
  useAdminUsageOverviewApi,
} from "hooks/useAdminApi";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";

/**
 * カテゴリ利用率バッジ
 * @param {{ cat: AdminCategoryUsage }} props カテゴリ
 * @return {JSX.Element} バッジ
 */
const UsageBadge: FC<{ cat: AdminCategoryUsage }> = ({ cat }) => {
  if (cat.limit === 0) return <span className="text-gray-400">-</span>;

  const pct = Math.round(cat.usageRate * 100);
  const isExceeded = cat.used >= cat.limit;
  const tone: BadgeTone = isExceeded
    ? "critical-error"
    : cat.isWarning
      ? "soft-warning"
      : "soft-success";

  return (
    <StatusBadge tone={tone}>
      {cat.used}/{cat.limit} ({pct}%)
    </StatusBadge>
  );
};

/**
 * 管理者向け利用状況ダッシュボードページ
 * @return {JSX.Element} ダッシュボードページ
 */
export const UsageDashboardPage: FC = () => {
  const [page, setPage] = useState(1);
  const {
    data: overview,
    loading: overviewLoading,
    error: overviewError,
  } = useAdminUsageOverviewApi({ page, limit: 20 });
  const { data: llmSummary, loading: llmLoading } = useAdminLLMSummaryApi();
  const { data: matchingStats, loading: matchingLoading } =
    useAdminMatchingStatsApi();

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>利用状況ダッシュボード</PageTitle>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin">← 管理画面に戻る</Link>
        </Button>
      </div>

      {/* テナント一覧テーブル */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">テナント別利用状況</h2>

          {overviewError && (
            <div className="alert alert-error mb-4">
              <span>エラー: {overviewError.message}</span>
            </div>
          )}

          {overviewLoading ? (
            <div className="flex justify-center py-8">
              <Loading size="lg" />
            </div>
          ) : overview && overview.items.length > 0 ? (
            <>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <Table.Root density="compact" zebra stickyHeader>
                  <Table.Head sticky>
                    <tr>
                      <Table.HeaderCell>テナント名</Table.HeaderCell>
                      <Table.HeaderCell>プラン</Table.HeaderCell>
                      <Table.HeaderCell>ステータス</Table.HeaderCell>
                      <Table.HeaderCell align="right">メール</Table.HeaderCell>
                      <Table.HeaderCell align="right">
                        マッチング
                      </Table.HeaderCell>
                      <Table.HeaderCell align="right">配信</Table.HeaderCell>
                      <Table.HeaderCell align="right">AI</Table.HeaderCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {overview.items.map(item => (
                      <Table.Row key={item.tenantId}>
                        <Table.Cell>
                          <UiLink to={`/admin/tenants/${item.tenantId}`}>
                            {item.tenantName}
                          </UiLink>
                        </Table.Cell>
                        <Table.Cell>{item.planName}</Table.Cell>
                        <Table.Cell>
                          <StatusBadge
                            tone={
                              item.status === "active"
                                ? "soft-success"
                                : item.status === "trial"
                                  ? "soft-info"
                                  : "soft-neutral"
                            }
                          >
                            {item.status}
                          </StatusBadge>
                        </Table.Cell>
                        {item.categories.map(cat => (
                          <Table.Cell key={cat.category} align="right">
                            <UsageBadge cat={cat} />
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </div>

              {/* ページネーション */}
              {overview.total > overview.limit && (
                <div className="flex justify-center mt-4">
                  <div className="join">
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      isJoinItem
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      前へ
                    </Button>
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      isJoinItem
                      disabled
                    >
                      {page} / {Math.ceil(overview.total / overview.limit)}
                    </Button>
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      isJoinItem
                      disabled={
                        page >= Math.ceil(overview.total / overview.limit)
                      }
                      onClick={() => setPage(p => p + 1)}
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-4">
              アクティブな契約を持つテナントがありません
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LLMコストサマリー */}
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-color-title">
              LLMコストサマリー（過去30日）
            </h2>

            {llmLoading ? (
              <div className="flex justify-center py-4">
                <Loading size="md" />
              </div>
            ) : llmSummary ? (
              <>
                <div className="stat bg-base-200 rounded-lg mb-4">
                  <div className="stat-title">総コスト</div>
                  <div className="stat-value text-primary text-2xl">
                    ${llmSummary.totalCostUsd.toFixed(4)}
                  </div>
                </div>

                {llmSummary.models.length > 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <Table.Root density="compact" zebra stickyHeader>
                      <Table.Head sticky>
                        <tr>
                          <Table.HeaderCell>モデル</Table.HeaderCell>
                          <Table.HeaderCell>操作</Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            回数
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            トークン
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            コスト
                          </Table.HeaderCell>
                        </tr>
                      </Table.Head>
                      <Table.Body>
                        {llmSummary.models.flatMap(model =>
                          model.operations.map(op => (
                            <Table.Row key={`${model.model}-${op.operation}`}>
                              <Table.Cell>{model.model}</Table.Cell>
                              <Table.Cell>{op.operation}</Table.Cell>
                              <Table.Cell align="right">
                                {op.callCount.toLocaleString()}
                              </Table.Cell>
                              <Table.Cell align="right">
                                {op.totalTokens.toLocaleString()}
                              </Table.Cell>
                              <Table.Cell align="right">
                                ${op.estimatedCostUsd.toFixed(4)}
                              </Table.Cell>
                            </Table.Row>
                          )),
                        )}
                      </Table.Body>
                    </Table.Root>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">データなし</p>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* マッチング統計 */}
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-color-title">
              マッチング統計（過去30日）
            </h2>

            {matchingLoading ? (
              <div className="flex justify-center py-4">
                <Loading size="md" />
              </div>
            ) : matchingStats ? (
              <>
                <div className="stat bg-base-200 rounded-lg mb-4">
                  <div className="stat-title">総マッチング数</div>
                  <div className="stat-value text-primary text-2xl">
                    {matchingStats.total.toLocaleString()}
                  </div>
                </div>

                {matchingStats.byType.length > 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <Table.Root density="compact" zebra stickyHeader>
                      <Table.Head sticky>
                        <tr>
                          <Table.HeaderCell>種別</Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            回数
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            割合
                          </Table.HeaderCell>
                        </tr>
                      </Table.Head>
                      <Table.Body>
                        {matchingStats.byType.map(t => (
                          <Table.Row key={t.triggerType}>
                            <Table.Cell>{t.triggerType}</Table.Cell>
                            <Table.Cell align="right">
                              {t.count.toLocaleString()}
                            </Table.Cell>
                            <Table.Cell align="right">{t.pct}%</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">データなし</p>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboardPage;
